import { ref, computed, watch, type Ref } from "vue";
// Import the new API functions and Panel type
import { fetchPanels, createPanel } from "@/services/api";
import type { Panel } from "../../../src/models/data-models";
import { useAuth } from "./useAuth";

// Define the type for the data needed to create a panel config
// Matches the type used in api.ts
// FIX: Use 'title' and 'instructions' instead of 'name' and 'standingQuery'
type CreatePanelConfigData = Pick<
  Panel,
  "title" | "instructions" | "agentType" | "settings" | "schedule"
>;

// Reactive state for panel configurations
const panelConfigs: Ref<Panel[]> = ref([]);
const isLoading = ref(false);
const error: Ref<Error | null> = ref(null);

// Function to load panel configurations from the API
async function loadPanelConfigs(isAuthenticated: Ref<boolean>) {
  if (!isAuthenticated.value) {
    console.log("User not authenticated, skipping panel config load.");
    panelConfigs.value = []; // Clear configs if user logs out
    return;
  }

  isLoading.value = true;
  error.value = null;
  console.log("Attempting to fetch panel configs...");
  try {
    // Fetch panel configurations directly
    panelConfigs.value = await fetchPanels();
    console.log("Successfully fetched panel configs:", panelConfigs.value);
  } catch (err) {
    console.error("Failed to load panel configs:", err);
    error.value = err instanceof Error ? err : new Error(String(err));
    panelConfigs.value = []; // Clear configs on error
  } finally {
    isLoading.value = false;
  }
}

// Function to create a new panel configuration
async function createPanelConfig(panelConfigData: CreatePanelConfigData, isAuthenticated: Ref<boolean>) {
  if (!isAuthenticated.value) {
    throw new Error("User must be authenticated to create a panel config.");
  }
  console.log("Attempting to create panel config:", panelConfigData);
  try {
    const newPanelConfig = await createPanel(panelConfigData);
    console.log("Successfully created panel config:", newPanelConfig);
    // Refresh the panel config list after successful creation
    await loadPanelConfigs(isAuthenticated);
    return newPanelConfig;
  } catch (err) {
    console.error("Failed to create panel config:", err);
    // Re-throw the error to be caught in the component
    throw err instanceof Error ? err : new Error(String(err));
  }
}

// Export the composable function
export function usePanels() {
  const { isAuthenticated, isInitialized } = useAuth();

  // Watch for initialization and sign-in status changes
  watch(
    [isInitialized, isAuthenticated],
    ([initialized, signedIn]) => {
      if (initialized && signedIn) {
        loadPanelConfigs(isAuthenticated); // Load configs when signed in
      } else {
        // Clear configs if user signs out or isn't initialized
        panelConfigs.value = [];
        error.value = null;
        isLoading.value = false;
      }
    },
    { immediate: true } // Check status immediately
  );

  return {
    // Expose panel configurations
    panels: computed(() => panelConfigs.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    // Expose load function
    loadPanels: () => loadPanelConfigs(isAuthenticated),
    // Expose create function
    createPanel: (panelConfigData: CreatePanelConfigData) =>
      createPanelConfig(panelConfigData, isAuthenticated),
  };
}
